import LoadingButton from '@mui/lab/LoadingButton';
import { DialogContent, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { OnlyFansSextfoceLiveStreamAnnoncementMessageGroup } from '../../../../../hooks/useSextforceLiveStreamAutoAnnouncementsMessages';
import { OnlyFansSubscriber } from '../../../../../hooks/useSubscriber';
import DialogAdaptive from '../../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../../common/StyledDialogActions';

type Props = {
    subscriber: OnlyFansSubscriber;
    initialValue?: OnlyFansSextfoceLiveStreamAnnoncementMessageGroup;
    onSubmit: (group: OnlyFansSextfoceLiveStreamAnnoncementMessageGroup) => void;
    onCancel: () => void;
    isLoading: boolean;
    open: boolean;
};

const SextforceLiveStreamAutoAnnouncementsMessageGroupForm = (props: Props) => {
    const { subscriber, initialValue, onSubmit, onCancel, isLoading, open } = props;

    const [name, setName] = useState<string>(initialValue?.name || '');
    const [announcementEveryAmount, setAnnouncementEveryAmount] = useState<number | undefined>(initialValue?.announcementEveryAmount || 60);
    const [announcementEveryUnit, setAnnouncementEveryUnit] = useState<moment.unitOfTime.DurationConstructor>(
        (initialValue?.announcementEveryUnit as moment.unitOfTime.DurationConstructor) || 'seconds',
    );

    useEffect(() => {
        if (initialValue) {
            setName(initialValue.name || '');
            setAnnouncementEveryAmount(initialValue.announcementEveryAmount || 60);
            setAnnouncementEveryUnit((initialValue.announcementEveryUnit as moment.unitOfTime.DurationConstructor) || 'seconds');
        }
    }, [initialValue]);

    const isValid = () => {
        if (!name.trim()) return false;

        if (announcementEveryAmount === undefined || announcementEveryAmount < 1) return false;

        // Minimum 20 seconds for the announcement interval
        if (
            (announcementEveryAmount < 20 && announcementEveryUnit === 'seconds') ||
            (announcementEveryAmount < 1 && announcementEveryUnit !== 'seconds')
        ) {
            return false;
        }

        return true;
    };

    const handleSubmit = () => {
        if (!isValid() || announcementEveryAmount === undefined || !subscriber._id) return;

        const groupData: OnlyFansSextfoceLiveStreamAnnoncementMessageGroup = {
            _id: initialValue?._id,
            subscriberId: subscriber._id,
            active: true,
            name: name.trim(),
            announcementEveryAmount,
            announcementEveryUnit,
            sortOrder: initialValue?.sortOrder,
        };

        onSubmit(groupData);
    };

    return (
        <DialogAdaptive open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <StyledCardTitleBar title={initialValue ? 'Update Message Group' : 'Create Message Group'} />
            <DialogContent>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <TextField
                            label="Group Name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            size="small"
                            fullWidth
                            disabled={isLoading}
                            placeholder="e.g., Welcome Messages, Tip Requests"
                            required
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12}>
                                <Typography variant="body1" color="text.secondary">
                                    Post announcement every:
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={6}>
                                        <TextField
                                            value={announcementEveryAmount || ''}
                                            onChange={e => {
                                                try {
                                                    if (e.target.value && isNaN(parseInt(e.target.value, 10))) {
                                                        return;
                                                    }
                                                } catch (error) {
                                                    return;
                                                }
                                                const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
                                                setAnnouncementEveryAmount(value);
                                            }}
                                            size="small"
                                            disabled={isLoading}
                                            sx={{ minWidth: '80px' }}
                                            type="number"
                                            inputProps={{ min: 1 }}
                                            required
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Select
                                            value={announcementEveryUnit}
                                            onChange={e => {
                                                setAnnouncementEveryUnit(e.target.value as moment.unitOfTime.DurationConstructor);
                                            }}
                                            size="small"
                                            disabled={isLoading}
                                            fullWidth
                                            sx={{ minWidth: '120px', mt: 1, mb: 0.5 }}
                                        >
                                            <MenuItem value="seconds">Seconds</MenuItem>
                                            <MenuItem value="minutes">Minutes</MenuItem>
                                            <MenuItem value="hours">Hours</MenuItem>
                                            <MenuItem value="days">Days</MenuItem>
                                        </Select>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {announcementEveryAmount !== undefined && announcementEveryAmount < 20 && announcementEveryUnit === 'seconds' && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                        Minimum interval is 20 seconds
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>

            <StyledDialogActions>
                <LoadingButton variant="outlined" color="secondary" size="small" onClick={onCancel} disabled={isLoading}>
                    <span>Cancel</span>
                </LoadingButton>
                <LoadingButton
                    variant="contained"
                    size="small"
                    onClick={handleSubmit}
                    loading={isLoading}
                    disabled={!isValid()}
                    color="primary"
                >
                    <span>{initialValue ? 'Update Group' : 'Create Group'}</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default SextforceLiveStreamAutoAnnouncementsMessageGroupForm;
